import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISSED_KEY = 'mt.install-banner.dismissed-at';
const REPROMPT_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

function wasRecentlyDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < REPROMPT_AFTER_MS;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function isIOSSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIOS && isSafari;
}

function shouldHideOnPath(pathname: string): boolean {
  // Only surface the install CTA on routes without a sticky bottom CTA of
  // their own (home, settings). Everywhere else — profile FAB stack, review
  // save bar, chat composer, add/manual forms, doctor share, auth — the
  // banner would overlap the primary action at `z-50`.
  return pathname !== '/' && pathname !== '/settings';
}

export function PWAInstallBanner() {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setHidden(true);
      return;
    }
    if (wasRecentlyDismissed()) {
      setHidden(true);
      return;
    }

    function onBeforeInstall(event: Event) {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    }

    function onInstalled() {
      setHidden(true);
      setDeferred(null);
      try {
        window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
      } catch {
        // localStorage unavailable — ignore.
      }
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    if (isIOSSafari()) {
      setShowIOSHint(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  function dismiss() {
    setHidden(true);
    setDeferred(null);
    setShowIOSHint(false);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted' || outcome === 'dismissed') {
      dismiss();
    }
  }

  if (hidden) return null;
  if (shouldHideOnPath(pathname)) return null;
  if (!deferred && !showIOSHint) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Caremory"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 safe-bottom"
    >
      <div className="max-w-md mx-auto card p-3 shadow-pop flex items-start gap-3">
        <div className="mt-0.5 h-9 w-9 flex-shrink-0 rounded-lg bg-accent-soft flex items-center justify-center">
          <svg
            className="h-5 w-5 text-accent"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">Install Caremory</p>
          {deferred ? (
            <p className="text-xs text-ink-muted mt-0.5">
              Add it to your home screen for quick access.
            </p>
          ) : (
            <p className="text-xs text-ink-muted mt-0.5">
              Tap <span aria-hidden="true">⬆︎</span> Share, then{' '}
              <span className="font-medium">Add to Home Screen</span>.
            </p>
          )}
          <div className="mt-2 flex gap-2">
            {deferred ? (
              <button
                type="button"
                onClick={install}
                className="btn-primary text-sm px-3"
              >
                Install
              </button>
            ) : null}
            <button
              type="button"
              onClick={dismiss}
              className="btn-secondary text-sm px-3"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="min-h-tap min-w-tap flex items-center justify-center rounded-md text-ink-muted hover:text-ink"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
